import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Drawer, DrawerTrigger } from "@/components/ui/drawer";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterIcon from "../../assets/icons/Filter";
import SvgClose from "../../assets/icons/SvgClose";
import { removePrice, resetFilter, updateFilter } from "../../slices/filter";
import BushButton from "../common/BushButton";
import Color from "./Color";
import MobileFilter from "./MobileFilter";
import {
  PRICE,
  SIZES,
} from "./constants";

function CustomCheckBox({ isChecked, label, name, onClick }) {
  return (
    <div className="w-fit items-start flex space-x-2 px-2 transition-transform duration-200 ease-linear hover:scale-[1.03] overflow-hidden">
      <Checkbox
        id={name}
        checked={isChecked}
        onCheckedChange={onClick}
        className=""
      />
      <label htmlFor={name} className="text-base">
        {label}
      </label>
    </div>
  );
}

const Filter = ({ colors, sizes }) => {
  const { filter } = useSelector((state) => state.filter);
  const [amount, setAmount] = useState(filter.price);
  const dispatch = useDispatch();
  const debounceFn = useCallback(debounce(handleDebounceFn, 1000), []);

  function handleClearAll() {
    dispatch(resetFilter());
  }

  function handleUpdateFilter(key, value) {
    dispatch(updateFilter({ key, value }));
  }

  function handleRemovePrice() {
    dispatch(removePrice());
  }

  function handleAmount(e) {
    setAmount((prevVal) => ({ ...prevVal, [e.target.name]: e.target.value }));
    debounceFn({ ...amount, [e.target.name]: e.target.value });
  }

  function handleDebounceFn(amount) {
    handleUpdateFilter(PRICE, amount);
  }

  useEffect(() => {
    setAmount(filter.price);
  }, [filter]);

  return (
    <div className="max-w-full lg:max-w-[20%] w-full pl-[10px] md:pl-5 pb-[10px] md:pb-5 grow shrink-0 box-border">
      <div className="flex flex-col-reverse lg:mt-[50px] pr-[35px] border-inherit">
        {/* Desktop Filter */}
        <div className="block border-inherit">
          {/* Filter Tags Section */}
          <div className="flex items-center flex-wrap w-full mt-5 lg:mt-0">
            {Object.entries(filter).map(([key, values], index) => {
              if (key === "price" && (values.min > 0 || values.max > 0)) {
                return (
                  <button
                    key={index}
                    className="text-[15px] font-bold tracking-wide my-[5px] mx-[5px] px-5 py-2 inline-flex items-center border-2 border-solid border-black
                    justify-center mt-[15px] rounded-full cursor-pointer text-black transition-all duration-200 ease-linear hover:scale-105"
                    onClick={handleRemovePrice}
                  >
                    <span className="mr-[5px]">{`$${values.min} - $${
                      values.max > 0 ? values.max : 29999
                    }`}</span>
                    <SvgClose className="w-[10px] h-[10px] mx-[5px] object-contain" />
                  </button>
                );
              } else if (values.length > 0) {
                return values.map((value, ind) => (
                  <button
                    key={ind}
                    className="text-[15px] font-bold tracking-wide my-[5px] mx-[5px] px-5 py-2 inline-flex items-center border-2 border-solid border-black
                    justify-center mt-[15px] rounded-full cursor-pointer text-black transition-all duration-200 ease-linear hover:scale-105"
                    onClick={() => handleUpdateFilter(key, value)}
                  >
                    <span className="mr-[5px]">{value}</span>
                    <SvgClose className="w-[10px] h-[10px] mx-[5px] object-contain" />
                  </button>
                ));
              }
            })}
            <button
              className="text-xs tracking-wide my-[5px] px-1 inline-flex items-center hover:bg-transparent hover:text-secondary-500
              font-normal underline"
              onClick={handleClearAll}
            >
              Clear all
            </button>
          </div>

          {/* Filters */}
          <Accordion type="multiple" className="w-full hidden lg:block">
            {/* Price */}
            <AccordionItem
              value="price"
              className="border-b border-solid border-b-black/10"
            >
              <h2 className="text-lg font-bold tracking-wide leading-7 bg-transparent">
                <AccordionTrigger className="py-5 cursor-pointer hover:no-underline">
                  <span className="text-lg leading-7 tracking-wide font-bold flex-1 text-left bg-transparent text-black">
                    Price
                  </span>
                </AccordionTrigger>
              </h2>
              <AccordionContent className="pb-[25px] text-sm tracking-wide leading-[25px]">
                <div className="flex gap-6">
                  <div className="gap-1 text-lg">
                    $
                    <div className="flex flex-col mt-[14px]">
                      <input
                        className="bg-[rgb(242,242,242)] w-[90%] px-[10px] border-b border-solid border-b-gray-700 placeholder:text-gray-700 h-10"
                        placeholder="0"
                        type="number"
                        id="min"
                        name="min"
                        key="min"
                        value={amount.min}
                        onChange={handleAmount}
                      />
                      <p className="text-xs text-gray-400 mt-[5px]">From</p>
                    </div>
                  </div>
                  <div className="gap-1 text-lg">
                    $
                    <div className="flex flex-col mt-[14px]">
                      <input
                        className="bg-[rgb(242,242,242)] w-[90%] px-[10px] border-b border-solid border-b-gray-700 placeholder:text-gray-700 h-10"
                        placeholder="29999"
                        type="number"
                        id="max"
                        name="max"
                        key="max"
                        value={amount.max}
                        onChange={handleAmount}
                      />
                      <p className="text-xs text-gray-400 mt-[5px]">To</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>{" "}
            {/* Colors */}
            <AccordionItem
              value="colors"
              className="border-b border-solid border-b-black/10"
            >
              <h2 className="text-lg font-bold tracking-wide leading-7 bg-transparent">
                <AccordionTrigger className="py-5 cursor-pointer hover:no-underline">
                  <span className="text-lg leading-7 tracking-wide font-bold flex-1 text-left bg-transparent text-black">
                    Colors
                  </span>
                </AccordionTrigger>
              </h2>
              <Color colorNames={colors} handleClick={handleUpdateFilter} />
            </AccordionItem>
            {/* Size */}
            <AccordionItem
              className="border-b border-solid border-b-black/10"
              value="sizes"
            >
              <h2 className="text-lg font-bold tracking-wide leading-7 bg-transparent">
                <AccordionTrigger className="py-5 cursor-pointer hover:no-underline">
                  <span className="text-lg leading-7 tracking-wide font-bold flex-1 text-left bg-transparent text-black">
                    Size
                  </span>
                </AccordionTrigger>
              </h2>
              <AccordionContent className="pb-[15px] text-sm tracking-wide leading-[25px]">
                {typeof sizes === "object" &&
                  Object.entries(sizes)?.map(([size, counts], index) => (
                    <BushButton
                      styles={`text-sm font-medium tracking-wide my-[6px] mx-[6px] px-[15px] py-[10px]
                       border border-solid border-[#f2f2f2] ${
                         filter.sizes?.includes(size)
                           ? "bg-black"
                           : "bg-[#f2f2f2]"
                       } ${
                         filter.sizes?.includes(size)
                           ? "text-white"
                           : "text-black"
                       }`}
                      key={index}
                      onClick={() => handleUpdateFilter(SIZES, size)}
                    >
                      {`${size} (${counts})`}
                    </BushButton>
                  ))}
              </AccordionContent>
            </AccordionItem>{" "}
          </Accordion>
        </div>
        {/* Mobile Filter */}
        <Drawer>
          <div className="flex items-center lg:hidden ">
            <DrawerTrigger asChild>
              <div className="flex items-center max-w-fit cursor-pointer hover:text-secondary-500 group">
                <FilterIcon width="50px" height="30px" />
                <span className="mx-[10px] group-hover:underline group-hover:outline-offset-[3px] text-black">
                  Filter
                </span>
              </div>
            </DrawerTrigger>
            <MobileFilter
              handleClearAll={handleClearAll}
              colors={colors}
              sizes={sizes}
            />
          </div>
        </Drawer>
      </div>
    </div>
  );
};

export default Filter;
